import React, { useMemo } from 'react';
import SelectInput from '../../../../../common/inputs/select-input';
import TextInput, { requiredValidatior } from '../../../../../topics/components/TextInput';
import { parseEventTime, stringifyGMTOffset } from '../../../helpers/event-time';
import rawTimeZones from '../../../helpers/time-zones';
import { LiveEventTime } from '../../../state/types';
import { LiveBlogModalAction, TAction } from '../reducer';
import DatePicker from './DatePicker';
interface Props {
  displayName: string;
  timezone: string;
  startTime: LiveEventTime;
  endTime: LiveEventTime;
  dispatch: React.Dispatch<LiveBlogModalAction>;
  timeError: any;
}

const TIME_ZONES = rawTimeZones
  .sort((timeA, timeB) => (timeA.label > timeB.label ? 1 : -1))
  .map(timeZone => ({
    value: timeZone.name,
    text: `(${stringifyGMTOffset(timeZone.offset)}) ${timeZone.label}`
  }));

const EventDetails: React.FC<Props> = ({
  displayName,
  startTime,
  endTime,
  dispatch,
  timezone,
  timeError
}) => {
  const timezoneOption = useMemo(() => TIME_ZONES.find(t => t.value === timezone), [
    timezone,
    rawTimeZones
  ]);
  const startTimeAsDate = useMemo(() => startTime && parseEventTime(startTime), [startTime]);
  const endTimeAsDate = useMemo(() => endTime && parseEventTime(endTime), [endTime]);

  const onEventDetailsChange = (value: string | Date, field: string) => {
    dispatch({ type: TAction.EventDetailsChange, payload: { [field]: value } });
  };

  const onDisplayNameChange = (displayName: string) => {
    onEventDetailsChange(displayName, 'displayName');
  };

  const onTimezoneChange = (timezone: { text: string; value: string }) => {
    onEventDetailsChange(timezone.value, 'timezone');
  };

  const onStartTimeChange = (startTime: Date) => {
    onEventDetailsChange(startTime, 'startTime');
  };

  const onEndTimeChange = (endTime: Date) => {
    onEventDetailsChange(endTime, 'endTime');
  };

  const errorMessage = useMemo(() => {
    const isTimeConstraintsError = !!timeError.error;

    if (isTimeConstraintsError) {
      return 'Your event needs to end after it starts.';
    } else {
      return null;
    }
  }, [timeError]);

  return (
    <div className={'event-details-container'}>
      <div className="fields">
        <TextInput
          className={'display-name-field'}
          title={'Descriptive name'}
          placeholder={'e.g Elections Blogging'}
          value={displayName}
          validatior={requiredValidatior}
          maxLength={60}
          onChange={onDisplayNameChange}
          validationMessage={'Add a descriptive name to help you identify the event'}
          withCircleError={false}
        />
        <SelectInput
          title={<h6>Time Zone</h6>}
          onChange={onTimezoneChange}
          options={TIME_ZONES}
          value={timezoneOption}
        />
        <div className={'time-pickers-wrapper'}>
          <DatePicker
            title={'Starts'}
            time={startTimeAsDate}
            startDate={new Date()}
            endDate={endTimeAsDate}
            onTimeChange={onStartTimeChange}
            error={timeError.field === 'start'}
          />
          <DatePicker
            title={'Ends'}
            time={endTimeAsDate}
            startDate={startTimeAsDate}
            onTimeChange={onEndTimeChange}
            error={timeError.field === 'end'}
          />
        </div>
        {timeError.error && <span className={'live-blog-error-message'}>{errorMessage}</span>}
      </div>
    </div>
  );
};

export default EventDetails;
